import React, { useState, useEffect } from 'react';
import axios from 'axios';

const CATEGORY_INFO = {
  general: { name: 'General', icon: '⚙️', description: 'Core system settings' },
  chat: { name: 'Chat & AI', icon: '💬', description: 'Chat and AI behavior settings' },
  reports: { name: 'Reports', icon: '📊', description: 'Report generation settings' },
  notifications: { name: 'Notifications', icon: '🔔', description: 'Alert and notification settings' },
  security: { name: 'Security', icon: '🔒', description: 'Security and access settings' },
  appearance: { name: 'Appearance', icon: '🎨', description: 'UI and theme settings' }
};

const GeneralSettings = () => {
  const [preferences, setPreferences] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [categories, setCategories] = useState([]);
  const [activeCategory, setActiveCategory] = useState('general');
  const [hasChanges, setHasChanges] = useState(false);
  const [toast, setToast] = useState({ show: false, message: '', type: 'success' });
  
  // Form state for unsaved changes
  const [editedPrefs, setEditedPrefs] = useState({});

  useEffect(() => {
    loadPreferences();
    loadCategories();
  }, []);

  const loadPreferences = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/admin/system-preferences`);
      setPreferences(response.data);
      
      // If no preferences exist, initialize defaults
      if (response.data.length === 0) {
        await initializeDefaults();
      }
    } catch (error) {
      console.error('Error loading preferences:', error);
      showToast('Failed to load settings', 'error');
    } finally {
      setLoading(false);
    }
  };

  const loadCategories = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/admin/system-preferences/categories`);
      setCategories(response.data);
    } catch (error) {
      console.error('Error loading categories:', error);
    }
  };

  const initializeDefaults = async () => {
    try {
      await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/admin/system-preferences/initialize`);
      await loadPreferences();
      showToast('Default settings initialized');
    } catch (error) {
      console.error('Error initializing defaults:', error);
    }
  };

  const showToast = (message, type = 'success') => {
    setToast({ show: true, message, type });
    setTimeout(() => setToast({ show: false, message: '', type: 'success' }), 4000);
  };

  const handleValueChange = (key, value) => {
    setEditedPrefs(prev => ({ ...prev, [key]: value }));
    setHasChanges(true);
  };

  const getEffectiveValue = (pref) => {
    return editedPrefs.hasOwnProperty(pref.key) ? editedPrefs[pref.key] : pref.value;
  };

  const parseValue = (value, dataType) => {
    switch (dataType) {
      case 'boolean':
        return value === 'true' || value === true;
      case 'number':
        return parseInt(value, 10) || 0;
      default:
        return value;
    }
  };

  const handleSave = async () => {
    if (Object.keys(editedPrefs).length === 0) return;
    
    setSaving(true);
    try {
      const updates = Object.entries(editedPrefs).map(([key, value]) => {
        const pref = preferences.find(p => p.key === key);
        return {
          key,
          value: String(value),
          data_type: pref?.data_type || 'string',
          category: pref?.category || 'general',
          description: pref?.description
        };
      });

      await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/system-preferences/bulk`,
        { preferences: updates }
      );
      
      showToast('Settings saved successfully');
      setEditedPrefs({});
      setHasChanges(false);
      await loadPreferences();
    } catch (error) {
      console.error('Error saving preferences:', error);
      showToast('Failed to save settings', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    setEditedPrefs({});
    setHasChanges(false);
  };

  const filteredPreferences = preferences.filter(p => p.category === activeCategory);

  const renderPreferenceInput = (pref) => {
    const value = getEffectiveValue(pref);
    
    switch (pref.data_type) {
      case 'boolean':
        return (
          <div className="flex items-center">
            <button
              onClick={() => handleValueChange(pref.key, value !== 'true' && value !== true)}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                parseValue(value, 'boolean') ? 'bg-indigo-600' : 'bg-slate-300'
              }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  parseValue(value, 'boolean') ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
            <span className="ml-3 text-sm text-slate-600">
              {parseValue(value, 'boolean') ? 'Enabled' : 'Disabled'}
            </span>
          </div>
        );
      
      case 'number':
        return (
          <input
            type="number"
            value={parseValue(value, 'number')}
            onChange={(e) => handleValueChange(pref.key, e.target.value)}
            className="w-32 px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-sm"
          />
        );
      
      default:
        return (
          <input
            type="text"
            value={value || ''}
            onChange={(e) => handleValueChange(pref.key, e.target.value)}
            className="w-64 px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-sm"
          />
        );
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-48">
        <div className="flex space-x-1">
          <div className="w-2 h-2 bg-emerald-500 rounded-full animate-bounce" style={{animationDelay: '0ms'}}></div>
          <div className="w-2 h-2 bg-emerald-500 rounded-full animate-bounce" style={{animationDelay: '150ms'}}></div>
          <div className="w-2 h-2 bg-emerald-500 rounded-full animate-bounce" style={{animationDelay: '300ms'}}></div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4">
      {/* Toast */}
      {toast.show && (
        <div className="fixed top-4 right-4 z-50 animate-slide-in">
          <div className={`${toast.type === 'success' ? 'bg-emerald-500' : 'bg-red-500'} text-white px-4 py-2 rounded-lg shadow-lg flex items-center space-x-2 text-sm`}>
            {toast.type === 'success' ? <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg> : <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>}
            <span>{toast.message}</span>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-lg font-bold text-slate-900">General Settings</h2>
          <p className="text-slate-500 text-xs mt-0.5">Manage system-wide preferences and feature toggles.</p>
        </div>
        {hasChanges && (
          <div className="flex items-center space-x-2">
            <span className="text-xs text-amber-600 bg-amber-50 px-2 py-1 rounded-full">Unsaved</span>
            <button onClick={handleReset} className="px-3 py-1.5 text-xs border border-slate-300 text-slate-700 rounded-lg font-medium hover:bg-slate-50">Reset</button>
            <button onClick={handleSave} disabled={saving}
              className="px-3 py-1.5 text-xs bg-gradient-to-r from-emerald-500 to-cyan-600 text-white rounded-lg font-medium hover:from-emerald-600 hover:to-cyan-700 disabled:opacity-50 flex items-center space-x-1">
              {saving ? (<><div className="animate-spin rounded-full h-3 w-3 border-b-2 border-white"></div><span>Saving...</span></>) : (<><svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg><span>Save</span></>)}
            </button>
          </div>
        )}
      </div>

      <div className="flex gap-4">
        {/* Category Sidebar */}
        <div className="w-48 flex-shrink-0">
          <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
            <div className="p-3 border-b border-slate-100"><h3 className="font-semibold text-xs text-slate-900">Categories</h3></div>
            <nav className="p-1.5">
              {Object.entries(CATEGORY_INFO).map(([key, info]) => {
                const count = preferences.filter(p => p.category === key).length;
                return (
                  <button key={key} onClick={() => setActiveCategory(key)}
                    className={`w-full flex items-center justify-between px-2 py-2 rounded-lg text-left transition-colors ${activeCategory === key ? 'bg-emerald-50 text-emerald-700' : 'text-slate-600 hover:bg-slate-50'}`}>
                    <div className="flex items-center space-x-2"><span className="text-sm">{info.icon}</span><span className="font-medium text-xs">{info.name}</span></div>
                    {count > 0 && <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${activeCategory === key ? 'bg-emerald-100' : 'bg-slate-100'}`}>{count}</span>}
                  </button>
                );
              })}
            </nav>
          </div>
          <div className="mt-3 bg-white rounded-lg border border-slate-200 p-3">
            <h3 className="font-semibold text-xs text-slate-900 mb-2">Quick Actions</h3>
            <button onClick={initializeDefaults} className="w-full px-3 py-1.5 text-xs border border-slate-300 text-slate-700 rounded-lg font-medium hover:bg-slate-50">Reset to Defaults</button>
          </div>
        </div>

        {/* Settings Content */}
        <div className="flex-1">
          <div className="bg-white rounded-lg border border-slate-200">
            <div className="p-4 border-b border-slate-100">
              <div className="flex items-center space-x-2">
                <span className="text-lg">{CATEGORY_INFO[activeCategory]?.icon}</span>
                <div><h3 className="text-sm font-semibold text-slate-900">{CATEGORY_INFO[activeCategory]?.name} Settings</h3><p className="text-xs text-slate-500">{CATEGORY_INFO[activeCategory]?.description}</p></div>
              </div>
            </div>
            <div className="divide-y divide-slate-100">
              {filteredPreferences.length === 0 ? (
                <div className="p-6 text-center">
                  <div className="w-10 h-10 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-2"><svg className="w-5 h-5 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /></svg></div>
                  <p className="text-slate-500 text-xs">No settings in this category</p>
                </div>
              ) : (
                filteredPreferences.map(pref => (
                  <div key={pref.key} className="p-4 flex items-center justify-between hover:bg-slate-50">
                    <div className="flex-1">
                      <div className="flex items-center space-x-2">
                        <h4 className="font-medium text-xs text-slate-900">{pref.key.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')}</h4>
                        {editedPrefs.hasOwnProperty(pref.key) && <span className="text-[10px] bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded-full">Modified</span>}
                      </div>
                      <p className="text-xs text-slate-500 mt-0.5">{pref.description}</p>
                    </div>
                    <div className="ml-3">{renderPreferenceInput(pref)}</div>
                  </div>
                ))
              )}
            </div>
          </div>
          <AddPreferenceSection activeCategory={activeCategory} onAdd={loadPreferences} showToast={showToast} />
        </div>
      </div>

      <style jsx>{`
        @keyframes slide-in {
          from { transform: translateX(100%); opacity: 0; }
          to { transform: translateX(0); opacity: 1; }
        }
        .animate-slide-in { animation: slide-in 0.3s ease-out; }
      `}</style>
    </div>
  );
};

// Separate component for adding new preferences
const AddPreferenceSection = ({ activeCategory, onAdd, showToast }) => {
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    key: '',
    value: '',
    data_type: 'string',
    description: ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.key.trim() || !formData.value.trim()) {
      showToast('Key and value are required', 'error');
      return;
    }

    try {
      await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/admin/system-preferences`, {
        ...formData,
        category: activeCategory
      });
      showToast('Setting added successfully');
      setShowForm(false);
      setFormData({ key: '', value: '', data_type: 'string', description: '' });
      onAdd();
    } catch (error) {
      console.error('Error adding preference:', error);
      showToast('Failed to add setting', 'error');
    }
  };

  return (
    <div className="mt-4">
      {!showForm ? (
        <button
          onClick={() => setShowForm(true)}
          className="w-full p-4 border-2 border-dashed border-slate-300 rounded-2xl text-slate-500 hover:border-indigo-400 hover:text-indigo-600 transition-colors flex items-center justify-center space-x-2"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          <span>Add Custom Setting</span>
        </button>
      ) : (
        <div className="bg-white rounded-2xl border border-slate-200 p-5">
          <h4 className="font-semibold text-slate-900 mb-4">Add New Setting</h4>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Key</label>
                <input
                  type="text"
                  value={formData.key}
                  onChange={(e) => setFormData({ ...formData, key: e.target.value.toLowerCase().replace(/\s+/g, '_') })}
                  placeholder="setting_key"
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-sm font-mono"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Data Type</label>
                <select
                  value={formData.data_type}
                  onChange={(e) => setFormData({ ...formData, data_type: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-sm bg-white"
                >
                  <option value="string">String</option>
                  <option value="boolean">Boolean</option>
                  <option value="number">Number</option>
                  <option value="json">JSON</option>
                </select>
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Value</label>
              <input
                type="text"
                value={formData.value}
                onChange={(e) => setFormData({ ...formData, value: e.target.value })}
                placeholder={formData.data_type === 'boolean' ? 'true or false' : 'Value'}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-sm"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Description</label>
              <input
                type="text"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="What does this setting control?"
                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-sm"
              />
            </div>
            <div className="flex justify-end space-x-3">
              <button
                type="button"
                onClick={() => setShowForm(false)}
                className="px-4 py-2 border border-slate-300 text-slate-700 rounded-lg font-medium hover:bg-slate-50 transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-4 py-2 bg-indigo-600 text-white rounded-lg font-medium hover:bg-indigo-700 transition-colors"
              >
                Add Setting
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
};

export default GeneralSettings;
